import { useCallback, useEffect, useRef, useState } from 'react'

import { clamp } from '../../../motion'

/**
 * Scroll-Engine der Showcase-Versionen.
 *
 * Wie der Pin-Modus von Version 2 (jede Section steht still, `--l` läuft von
 * 0 bis 1), aber mit Trägheit: Die Engine folgt der Scrollposition weich und
 * läuft dafür als durchgehende rAF-Schleife. `onFrame` bekommt pro Frame die
 * geglättete Position, die Zeit und das Layout, damit hängt sich das
 * Partikelfeld an denselben Takt.
 */
export function useShowStory(count, { onFrame } = {}) {
  const chapterRefs = useRef([])
  const sceneRefs = useRef([])
  const storyRef = useRef(null)
  const progressRef = useRef(null)
  const subscribers = useRef(new Map())
  const lastL = useRef([])
  const frameRef = useRef(onFrame)
  const [active, setActive] = useState(0)
  frameRef.current = onFrame

  const subscribe = useCallback((index, cb) => {
    if (!subscribers.current.has(index))
      subscribers.current.set(index, new Set())
    const set = subscribers.current.get(index)
    set.add(cb)
    cb(lastL.current[index] ?? 0)
    return () => set.delete(cb)
  }, [])

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)')
    let raf = 0
    let y = window.scrollY
    let last = performance.now()
    let current = -1
    let layout = []
    let story = { top: 0, height: 1 }

    const measure = () => {
      const sy = window.scrollY
      layout = chapterRefs.current.map((el) => {
        if (!el) return null
        const r = el.getBoundingClientRect()
        return { top: r.top + sy, height: r.height }
      })
      if (storyRef.current) {
        const r = storyRef.current.getBoundingClientRect()
        story = { top: r.top + sy, height: r.height }
      }
    }

    const tick = (now) => {
      const dt = Math.min(64, now - last)
      last = now
      const target = window.scrollY
      // Bildratenunabhängiges Nachziehen, bei reduzierter Bewegung sofort
      const k = reduce.matches ? 1 : 1 - Math.pow(1 - 0.12, dt / 16.7)
      y += (target - y) * k
      if (Math.abs(target - y) < 0.2) y = target

      const vh = window.innerHeight
      let best = 0
      for (let i = 0; i < count; i++) {
        const box = layout[i]
        const scene = sceneRefs.current[i]
        if (!box || !scene) continue
        const top = box.top - target
        const onScreen = top < vh && top + box.height > 0
        scene.style.visibility = onScreen ? 'visible' : 'hidden'
        if (box.top - y <= vh / 2) best = i
        const l = clamp((y - box.top) / Math.max(1, box.height - vh))
        if (lastL.current[i] === l) continue
        lastL.current[i] = l
        scene.style.setProperty('--l', l.toFixed(4))
        subscribers.current.get(i)?.forEach((cb) => cb(l))
      }

      if (progressRef.current) {
        const p = clamp((y + vh / 2 - story.top) / story.height)
        progressRef.current.style.setProperty('--story', p.toFixed(4))
      }
      if (best !== current) {
        current = best
        setActive(best)
      }
      frameRef.current?.({ y, now, vh, layout, l: lastL.current })
      raf = requestAnimationFrame(tick)
    }

    measure()
    const ro = new ResizeObserver(measure)
    if (storyRef.current) ro.observe(storyRef.current)
    window.addEventListener('resize', measure)
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [count])

  const scrollToChapter = useCallback((index) => {
    const el = chapterRefs.current[index]
    if (!el) return
    const top = el.getBoundingClientRect().top + window.scrollY
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' })
  }, [])

  return {
    active,
    chapterRefs,
    sceneRefs,
    storyRef,
    progressRef,
    subscribe,
    scrollToChapter
  }
}
